import { forwardRef, useImperativeHandle, ChangeEvent, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import { SectionTitle } from '../title/SectionTitle'
import { BtnWhiteRounded } from '../../components/Button'
import { InputText, InputTextDiv, InputTextSection, InputTextTitle } from '../input/InputText'
import { InputTextArea } from '../../components/input/InputTextArea'
import { ToggleRadioButton } from '../input/InputRadioButton'
import Dropdown from '../../components/dropdown/Dropdown'
import AddressListModal from '../../components/order/AddressListModal'
import AddressFinderModal from '../../components/order/AddressFinderModal'

import {
  // RecentAddressType,
  AddressItem,
} from '../../@types/order'

type OrderAddressSectionProps = {
  hasAccessToken?: boolean
  userDefaultAddress?: AddressItem | null
  setUserDefaultAddress: (address: AddressItem | null) => void
  onReady?: (isReady: boolean) => void
  onChange?: false | (() => void)
}

type AddressError = {
  receiverName?: boolean
  receiverZipCd?: boolean
  receiverAddress_1?: boolean
  receiverAddress_2?: boolean
  receiverPhone?: boolean
}

const OrderAddressSection = forwardRef(
  (
    { hasAccessToken = false, userDefaultAddress, setUserDefaultAddress, onReady, onChange }: OrderAddressSectionProps,
    ref
  ) => {
    // State
    const [isSelectedDefaultAddress, setIsSelectedDefaultAddress] = useState<boolean>(true)
    const [visibleAddressList, setVisibleAddressList] = useState<boolean>(false)
    const [visibleAddressFinder, setVisibleAddressFinder] = useState<boolean>(false)

    const [receiverName, setReceiverName] = useState<string>('')
    const [receiverZipCd, setReceiverZipCd] = useState<string>('')
    const [receiverAddress, setReceiverAddress] = useState<string>('')
    const [receiverDetailAddress, setReceiverDetailAddress] = useState<string>('')
    const [receiverPhone, setReceiverPhone] = useState<string>('')
    const [receiverMemo, setReceiverMemo] = useState<string>('')
    const [receiverMemoCustom, setReceiverMemoCustom] = useState<string>('')
    const [isFocus, setIsFocus] = useState<boolean>(false)
    const [error, setError] = useState<AddressError>({})

    const [addressNo, setAddressNo] = useState<number>(0)

    // Ref
    const deliveryNameRef = useRef<HTMLInputElement>(null)
    const addressFindButtonRef = useRef<HTMLButtonElement>(null)
    const deliveryAddressRef = useRef<HTMLInputElement>(null)
    const deliveryAddressDetailRef = useRef<HTMLInputElement>(null)
    const deliveryPhoneRef = useRef<HTMLInputElement>(null)
    // const receiverMemoCustomRef = useRef<HTMLTextAreaElement>(null)

    // 각 필드별 이전 값 저장용 ref
    const prevReceiverName = useRef(receiverName)
    const prevReceiverDetailAddress = useRef(receiverDetailAddress)
    const prevReceiverPhone = useRef(receiverPhone)

    // Memo
    const deliveryMemo = useMemo(
      () => ['문 앞에 놓아주세요', '부재 시 연락 부탁드려요', '배송 전 미리 연락해 주세요', '직접 입력'],
      []
    )

    const handleAddressListComplete = (data: AddressItem) => {
      setReceiverName(data.receiverName || '')
      setReceiverZipCd(data.receiverZipCd || '')
      setReceiverAddress(data.receiverAddress || '')
      setReceiverDetailAddress(data.receiverDetailAddress || '')
      setReceiverPhone(data.receiverContact1 || '')
      setAddressNo(data.addressNo || 0)

      // 배송메모 처리
      if (data.addressMemo) {
        if (deliveryMemo.includes(data.addressMemo)) {
          setReceiverMemo(data.addressMemo)
          setReceiverMemoCustom('')
        } else {
          setReceiverMemo('직접 입력')
          setTimeout(() => {
            setReceiverMemoCustom(data.addressMemo ?? '')
          }, 0)
        }
      } else {
        setReceiverMemo('')
        setReceiverMemoCustom('')
      }

      setVisibleAddressList(false)
      setIsSelectedDefaultAddress(data.defaultYn === 'Y')
      if (!onChange) return
      onChange?.()
    }

    const handleComplete = (data: any) => {
      console.log('handleComplete data >> ', data)
      const { address, userSelectedType, bname, buildingName, zonecode, jibunAddress } = data
      const extra = [bname, buildingName].filter(Boolean).join(', ')
      const fullAddress = userSelectedType === 'R' && extra ? `${address} (${extra})` : jibunAddress
      setReceiverZipCd(zonecode)
      setReceiverAddress(fullAddress)
      setReceiverDetailAddress('')
      setVisibleAddressFinder(false)
      setError((prev) => ({
        ...prev,
        receiverZipCd: false,
        receiverAddress_1: false,
        receiverAddress_2: false, // 상세주소도 초기화하므로 해제
      }))
      if (!onChange) return
      onChange?.()
    }

    // 최근배송지 바로적용 토글
    const onClickRecentAddress = useCallback(() => {
      setAddressNo(0)
      if (!hasAccessToken) return
      setIsSelectedDefaultAddress((prev) => {
        const next = !prev
        if (next && userDefaultAddress) {
          setReceiverName(userDefaultAddress.receiverName || '')
          setReceiverZipCd(userDefaultAddress.receiverZipCd || '')
          setReceiverAddress(userDefaultAddress.receiverAddress || '')
          setReceiverDetailAddress(userDefaultAddress.receiverDetailAddress || '')
          setReceiverPhone(userDefaultAddress.receiverContact1 || '')
          setAddressNo(userDefaultAddress.addressNo || 0)
        }
        return next
      })
    }, [hasAccessToken, userDefaultAddress, setReceiverZipCd, setReceiverAddress, setReceiverDetailAddress])

    // 실시간 입력 핸들러
    const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setReceiverName(value)
      setError((prev) => ({
        ...prev,
        receiverName: value.trim() === '',
      }))
      setAddressNo(0)
      setIsSelectedDefaultAddress(false)
    }, [])

    // 상세주소 입력 핸들러
    const handleAddress2Change = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setReceiverDetailAddress(value)
        setError((prev) => ({
          ...prev,
          receiverAddress_2: value.trim() === '',
        }))
        setAddressNo(0)
        setIsSelectedDefaultAddress(false)
      },
      [setReceiverDetailAddress]
    )

    const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setReceiverPhone(value)
      setError((prev) => ({
        ...prev,
        receiverPhone: !/^\d{10,11}$/.test(value.replace(/-/g, '')),
      }))
      setAddressNo(0)
      setIsSelectedDefaultAddress(false)
    }, [])

    // 이름 onBlur
    const handleNameBlur = useCallback(() => {
      if (prevReceiverName.current !== receiverName) {
        prevReceiverName.current = receiverName
        if (!onChange) return
        onChange?.()
      }
    }, [receiverName, onChange])

    // 상세주소 onBlur
    const handleAddress2Blur = useCallback(() => {
      if (prevReceiverDetailAddress.current !== receiverDetailAddress) {
        prevReceiverDetailAddress.current = receiverDetailAddress
        if (!onChange) return
        onChange?.()
      }
      setIsFocus(false)
      setAddressNo(0)
    }, [receiverDetailAddress, onChange])

    // 휴대폰 onBlur
    const handlePhoneBlur = useCallback(() => {
      const onlyNumber = receiverPhone.replace(/[^0-9]/g, '')
      setReceiverPhone(onlyNumber)
      if (prevReceiverPhone.current !== onlyNumber) {
        prevReceiverPhone.current = onlyNumber
        if (!onChange) return
        onChange?.()
      }
    }, [receiverPhone, onChange])

    // 유효성 검사 함수
    const validate = useCallback(() => {
      if (!receiverName) {
        setError((prev) => ({ ...prev, receiverName: true }))
        deliveryNameRef.current?.focus()
        alert('받으시는 분 성명을 입력해 주세요.')
        return false
      }
      if (!receiverZipCd) {
        setError((prev) => ({ ...prev, receiverZipCd: true }))
        addressFindButtonRef.current?.focus()
        alert('우편번호를 입력해 주세요.')
        return false
      }
      if (!receiverAddress) {
        setError((prev) => ({ ...prev, receiverAddress_1: true }))
        addressFindButtonRef.current?.focus()
        alert('주소를 입력해 주세요.')
        return false
      }
      if (!receiverDetailAddress) {
        setError((prev) => ({ ...prev, receiverAddress_2: true }))
        deliveryAddressDetailRef.current?.focus()
        alert('상세 주소를 입력해 주세요.')
        return false
      }
      if (!receiverPhone || !/^\d{10,11}$/.test(receiverPhone.replace(/-/g, ''))) {
        setError((prev) => ({ ...prev, receiverPhone: true }))
        deliveryPhoneRef.current?.focus()
        alert('휴대폰 번호를 정확히 입력해 주세요.')
        return false
      }
      return true
    }, [receiverName, receiverZipCd, receiverAddress, receiverDetailAddress, receiverPhone])

    useImperativeHandle(
      ref,
      () => ({
        validate,
        getValues: () => ({
          receiverName,
          receiverZipCd: receiverZipCd,
          receiverAddress_1: receiverAddress,
          receiverAddress_2: receiverDetailAddress,
          receiverPhone,
          receiverMemo,
        }),
      }),
      [receiverName, receiverZipCd, receiverAddress, receiverDetailAddress, receiverPhone, receiverMemo, validate]
    )

    useEffect(() => {
      if (userDefaultAddress) {
        prevReceiverName.current = userDefaultAddress.receiverName || ''
        prevReceiverDetailAddress.current = userDefaultAddress.receiverDetailAddress || ''
        prevReceiverPhone.current = userDefaultAddress.receiverContact1 || ''
      }
    }, [userDefaultAddress])

    // 기본 배송지 선택 시 자동입력
    useEffect(() => {
      if (!isSelectedDefaultAddress || !userDefaultAddress) return

      setReceiverName(userDefaultAddress.receiverName || '')
      setReceiverZipCd(userDefaultAddress.receiverZipCd || '')
      setReceiverAddress(userDefaultAddress.receiverAddress || '')
      setReceiverDetailAddress(userDefaultAddress.receiverDetailAddress || '')
      setReceiverPhone(userDefaultAddress.receiverContact1 || '')
      setAddressNo(userDefaultAddress.addressNo || 0)

      // 배송메모 처리
      if (userDefaultAddress.addressMemo) {
        if (deliveryMemo.includes(userDefaultAddress.addressMemo)) {
          setReceiverMemo(userDefaultAddress.addressMemo)
          setReceiverMemoCustom('')
        } else {
          setReceiverMemo('직접 입력')
          setReceiverMemoCustom(userDefaultAddress.addressMemo)
        }
      } else {
        setReceiverMemo('')
        setReceiverMemoCustom('')
      }
    }, [isSelectedDefaultAddress, userDefaultAddress, setReceiverZipCd, setReceiverAddress, setReceiverDetailAddress])

    useEffect(() => {
      const isReady =
        !!receiverName &&
        !!receiverZipCd &&
        !!receiverAddress &&
        !!receiverDetailAddress &&
        !!receiverPhone &&
        /^\d{10,11}$/.test(receiverPhone.replace(/-/g, ''))

      onReady?.(isReady)
    }, [receiverName, receiverZipCd, receiverAddress, receiverDetailAddress, receiverPhone, onReady])

    return (
      <Section>
        <FlexSection $hasAccessToken={hasAccessToken}>
          <SectionTitle>배송지 정보</SectionTitle>
          {hasAccessToken && (
            <BtnWhiteRounded style={{ marginLeft: 'auto' }} onClick={() => setVisibleAddressList(true)}>
              배송지 목록
            </BtnWhiteRounded>
          )}
        </FlexSection>

        {hasAccessToken && (
          <FlexSection>
            <div className='flex-section-inner' style={{ marginBottom: '14px' }}>
              <ToggleRadioButton
                name='defaultAddress'
                value='Y'
                checked={isSelectedDefaultAddress}
                onChange={() => onClickRecentAddress()}
                label='기본배송지 사용'
                size='small'
              />
            </div>
          </FlexSection>
        )}
        <InputTextSection>
          <InputTextDiv>
            <InputTextTitle>받으시는 분</InputTextTitle>
            <StyledInputText
              ref={deliveryNameRef}
              placeholder='성명을 입력해주세요.'
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              value={receiverName}
              $error={error.receiverName}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle style={{ alignSelf: 'start', marginTop: 10 }}>주소</InputTextTitle>
            <AddressInputGroup>
              <AddressInputRows>
                <AddressZipRow>
                  <AddressInputField
                    ref={deliveryAddressRef}
                    type='text'
                    id='address_1'
                    name='address_1'
                    placeholder='우편번호를 입력해주세요'
                    readOnly
                    value={receiverZipCd}
                    $error={error.receiverZipCd}
                    $isPostField
                  />
                  <AddressFindButton
                    ref={addressFindButtonRef}
                    type='button'
                    id='address_btn'
                    onClick={() => setVisibleAddressFinder(true)}
                  >
                    우편번호 찾기
                  </AddressFindButton>
                </AddressZipRow>
                <AddressInputField
                  type='text'
                  id='address_2'
                  name='address_2'
                  placeholder='주소를 입력해주세요'
                  readOnly
                  value={receiverAddress}
                  $error={error.receiverAddress_1}
                />
              </AddressInputRows>
              <StyledInputText
                ref={deliveryAddressDetailRef}
                type='text'
                id='address_3'
                name='address_3'
                placeholder='상세주소를 입력해주세요'
                value={receiverDetailAddress}
                $focus={isFocus}
                $error={error.receiverAddress_2}
                $addressInput
                onChange={handleAddress2Change}
                onFocus={() => setIsFocus(true)}
                onBlur={handleAddress2Blur}
              />
            </AddressInputGroup>
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle>휴대폰 번호</InputTextTitle>
            <StyledInputText
              ref={deliveryPhoneRef}
              name='receiverPhone'
              autoComplete='tel'
              placeholder='‘-’을 제외하고 입력해주세요.'
              value={receiverPhone.replaceAll('-', '')}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              $error={error.receiverPhone}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle style={{ alignSelf: 'start', marginTop: 10 }}>배송 메시지</InputTextTitle>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: 8,
              }}
            >
              <Dropdown setReceiverMemo={setReceiverMemo} data={deliveryMemo} value={receiverMemo} />
              {receiverMemo === '직접 입력' && (
                <InputTextArea
                  value={receiverMemoCustom}
                  onChange={(e) => setReceiverMemoCustom(e.target.value)}
                  placeholder='요청사항을 입력해 주세요. (30자 이내)'
                  maxLength={30}
                />
              )}
            </div>
          </InputTextDiv>
        </InputTextSection>

        <AddressListModal
          visible={visibleAddressList}
          addressNo={addressNo}
          setUserDefaultAddress={setUserDefaultAddress}
          onClose={() => setVisibleAddressList(false)}
          onComplete={handleAddressListComplete}
        />

        <AddressFinderModal
          visible={visibleAddressFinder}
          onClose={() => setVisibleAddressFinder(false)}
          onComplete={handleComplete}
        />
      </Section>
    )
  }
)

const FlexSection = styled.div<{ $hasAccessToken?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ $hasAccessToken }) => ($hasAccessToken ? '20px' : '0')};
  .flex-section-inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`

const Section = styled.div`
  padding: 24px 20px;
  @media screen and (min-width: 768px) {
    padding: 32px 0 32px 0;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

// 주소 입력 영역 스타일
const AddressInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`

const AddressInputRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AddressZipRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`

const AddressFindButton = styled.button`
  flex: 0 0 102px;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: var(--colWhite);
  padding: 12px 14px;
  border-radius: 5px;
  max-width: 100px;
  width: 102px;
  background-color: var(--colBlack);
  border: 0;
  font-family: Pretendard, sans-serif;
  transition: background 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    background: #23272f;
    color: var(--colWhite);
    outline: 2px solid #4b4b4b;
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.12);
  }
`

const AddressInputField = styled.input<{
  $error?: boolean
  $focus?: boolean
  $isPostField?: boolean
  $addressInput?: boolean
}>`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ $focus, $addressInput }) => ($focus || $addressInput ? 'var(--colBlack)' : 'var(--colGray6)')};
  padding: 10px 14px;
  border-radius: 5px;
  border: 1px solid ${({ $error, $focus }) => ($error ? 'red' : $focus ? 'var(--colBlack)' : 'var(--colGray3)')};
  background-color: ${({ readOnly }) => (readOnly ? 'var(--colGray1)' : 'var(--colWhite)')};
  flex: 1 1 auto;
  width: ${({ $isPostField }) => ($isPostField ? 'calc(100% - 110px)' : '100%')};
  min-width: 0;
`

const StyledInputText = styled(InputText)<{ $error?: boolean; $focus?: boolean; $addressInput?: boolean }>`
  ${({ $error }) =>
    $error &&
    css`
      border-color: red !important;
      outline: none;
    `}
`

OrderAddressSection.displayName = 'OrderAddressSection'
export default OrderAddressSection
